import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardActions,
  CardContent,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import axios, { pluckData } from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { BarChart } from '@mui/x-charts'
import { SearchIcon } from '../../core/icons'
import {
  fetchLinkedServices,
  fetchUsers,
  selectLinkedServiceById,
  selectLinkedServices,
  selectLinkedServicesStatus,
  selectUserByEmail,
  selectUsersByLinkedService,
  selectUsersStatus,
} from '../manage.slice'

type CEBreakdown = Record<
  string,
  {
    licenseType: string
  } & { [section: string]: number }
>

type CETotal = Record<
  string,
  {
    licenseType: string
  } & { [section: string]: number }
>

type UserCEBreakdown = Record<
  string,
  {
    [section: string]: number
  }
>

export default () => {
  let dispatch = useAppDispatch()
  let [linkedServiceDropdownOpen, setLinkedServiceDropdownOpen] =
    useState(false)
  let [userDropdownOpen, setUserDropdownOpen] = useState(false)
  let [userTotalDropdownOpen, setUserTotalDropdownOpen] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  let [_, setSearchValue] = useState<string>('')
  let linkedServices = useAppSelector(selectLinkedServices)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let [selectedLinkedServiceID, setSelectedLinkedServiceID] = useState('')
  let selectedLinkedService = useAppSelector(
    selectLinkedServiceById(selectedLinkedServiceID),
  )
  let users = useAppSelector(
    selectUsersByLinkedService(selectedLinkedServiceID),
  )
  let usersStatus = useAppSelector(selectUsersStatus)
  let [selectedUserEmail, setSelectedUserEmail] = useState('')
  let user = useAppSelector(selectUserByEmail(selectedUserEmail))
  let [timeframes, setTimeframes] = useState<string[]>([])
  let [sectionNames, setSectionNames] = useState<string[]>([])
  let [ceBreakdown, setCEBreakdown] = useState<CEBreakdown>()
  let [ceTotal, setCETotal] = useState<CETotal>()
  let [userCEBreakdown, setUserCEBreakdown] = useState<UserCEBreakdown>()
  let [userCETotal, setUserCETotal] = useState<CETotal>()
  let [userSectionNames, setUserSectionNames] = useState<string[]>([])
  let [errorMessage, setErrorMessage] = useState<string>('')
  let [selectedCEBreakdownTimeframe, setSelectedCEBreakdownTimeframe] =
    useState<string>('')

  useEffect(() => {
    if (linkedServicesStatus === 'uninitialized') {
      dispatch(fetchLinkedServices())
    }
  }, [linkedServicesStatus])

  useEffect(() => {
    if (usersStatus === 'uninitialized') {
      dispatch(fetchUsers())
    }
  }, [usersStatus])

  let getCEBreakdown = () =>
    Promise.resolve().then(() =>
      axios
        .get(`/api/manage/ce-record/${selectedLinkedServiceID}/hours/breakdown`)
        .then(pluckData)
        .then((res) => {
          setTimeframes(res.map((item: any) => item[0]))

          let allSectionKeys: string[] = Array.from(
            // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-shadow
            res.reduce((acc: Set<string>, [_, dataset]: [string, any[]]) => {
              dataset.forEach((entry) =>
                Object.keys(entry)
                  .filter((key) => key !== 'licenseType')
                  .forEach((key) => acc.add(key)),
              )
              return acc
            }, new Set<string>()),
          )
          setSectionNames(allSectionKeys)

          let sectionDefaults = Object.fromEntries(
            allSectionKeys.map((key) => [key, 0]),
          )

          let filled = Object.fromEntries(
            res.map(([timeFrame, dataset]: [string, any[]]) => [
              timeFrame,
              dataset.map((entry: any) => ({
                ...sectionDefaults,
                ...entry,
                ...Object.fromEntries(
                  allSectionKeys.map((key) => [
                    key,
                    entry[key] == null ? 0 : entry[key],
                  ]),
                ),
              })),
            ]),
          )

          setSelectedCEBreakdownTimeframe('Past 6 Months')
          setCEBreakdown(filled as CEBreakdown)
        })
        .catch((err) =>
          setErrorMessage(
            `CE Breakdown failed to load: ${err.response?.data || err}`,
          ),
        ),
    )

  let getCETotal = () =>
    Promise.resolve().then(() =>
      axios
        .get(`/api/manage/ce-record/${selectedLinkedServiceID}/hours/total`)
        .then(pluckData)
        .then((res) => {
          setCETotal(
            res.reduce(
              (
                acc: Record<string, number>,
                [timeFrame, [value]]: [string, number[]],
              ) => {
                // eslint-disable-next-line fp/no-mutation
                acc[timeFrame] = value
                return acc
              },
              {},
            ),
          )
        })
        .catch((err) =>
          setErrorMessage(
            `CE Totals failed to load: ${err.response?.data || err}`,
          ),
        ),
    )

  let getUserCEBreakdown = () =>
    Promise.resolve().then(() =>
      axios
        .get(
          `/api/manage/ce-record/${selectedLinkedServiceID}/hours/breakdown/user/${user?.id}`,
        )
        .then(pluckData)
        .then((res) => {
          let allSectionNames = new Set<string>()
          res.forEach(([, dataset]: [string, any[]]) => {
            dataset.forEach((entry) => {
              Object.keys(entry).forEach((key) => allSectionNames.add(key))
            })
          })

          let sectionDefaults = Object.fromEntries(
            Array.from(allSectionNames).map((key) => [key, 0]),
          )

          let flattened = res.map(([timeFrame, dataset]: [string, any[]]) => {
            let combined = dataset.reduce(
              (acc, entry) => {
                Object.entries(entry).forEach(([key, value]) => {
                  // eslint-disable-next-line fp/no-mutation
                  acc[key] = (acc[key] ?? 0) + (Number(value) || 0)
                })
                return acc
              },
              { ...sectionDefaults } as Record<string, number>,
            )
            return { timeFrame, ...combined }
          })

          setUserSectionNames(Array.from(allSectionNames))

          setUserCEBreakdown(
            flattened.reduce(
              (acc: any, item: any) => {
                // eslint-disable-next-line fp/no-mutation
                acc[item.timeFrame] = [item]
                return acc
              },
              {} as Record<string, any[]>,
            ),
          )
        })
        .catch((err) =>
          setErrorMessage(
            `CE Breakdown failed to load: ${err.response?.data || err}`,
          ),
        ),
    )

  let getUserCETotal = () =>
    Promise.resolve().then(() =>
      axios
        .get(
          `/api/manage/ce-record/${selectedLinkedServiceID}/hours/total/user/${user?.id}`,
        )
        .then(pluckData)
        .then((res) => {
          setUserCETotal(
            res.reduce(
              (
                acc: Record<string, number>,
                [timeFrame, [value]]: [string, number[]],
              ) => {
                // eslint-disable-next-line fp/no-mutation
                acc[timeFrame] = value
                return acc
              },
              {},
            ),
          )
        })
        .catch((err) =>
          setErrorMessage(
            `CE Breakdown failed to load: ${err.response?.data || err}`,
          ),
        ),
    )

  useEffect(() => {
    if (!selectedLinkedServiceID) {
      setSelectedLinkedServiceID(linkedServices[0]?.id)
    }
    if (selectedLinkedServiceID) {
      getCEBreakdown()
      getCETotal()
    }
  }, [selectedLinkedServiceID])

  useEffect(() => {
    if (selectedLinkedServiceID && selectedUserEmail) {
      setUserCEBreakdown(undefined)
      setUserCETotal(undefined)
      setUserSectionNames([])
      getUserCEBreakdown()
      getUserCETotal()
    }
  }, [selectedUserEmail, selectedLinkedServiceID])

  let handleSelectedCEBreakdownTimeframeChange = (
    // eslint-disable-next-line no-shadow
    _: React.MouseEvent<HTMLElement>,
    newChart: string | null,
  ) => {
    setSelectedCEBreakdownTimeframe(newChart as string)
  }

  return (
    <>
      <Container maxWidth="xl">
        <Typography variant="h5" sx={{ py: 4 }}>
          CE Analytics
        </Typography>
        <Autocomplete
          open={linkedServiceDropdownOpen}
          onOpen={() => {
            setLinkedServiceDropdownOpen(true)
          }}
          onClose={() => {
            setLinkedServiceDropdownOpen(false)
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={(option) => `${option.name} | ${option.id}`}
          // eslint-disable-next-line no-shadow
          onInputChange={(_, value) => {
            setSelectedLinkedServiceID(value.split(' | ')[1])
          }}
          options={linkedServices}
          loading={linkedServicesStatus === 'pending'}
          renderInput={(params) => (
            <TextField
              {...params}
              onChange={(e) => setSearchValue(e.target.value)}
              label="Select a Linked Service"
              slotProps={{
                ...params.slotProps,

                input: {
                  ...params.slotProps.input,
                  endAdornment: (
                    <>
                      {<SearchIcon sx={{ cursor: 'pointer' }} />}
                      {params.slotProps.input.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />
        {selectedLinkedService && (
          <>
            <Box className="grid grid-cols-6 gap-4 mt-4">
              {!ceTotal ? (
                <>
                  <Box className="col-span-6 md:col-span-3">
                    <Skeleton
                      sx={{
                        bgcolor: 'grey.900',
                        height: 244,
                      }}
                      variant="rectangular"
                    />
                  </Box>
                  <Box className="col-span-6 md:col-span-3">
                    <Skeleton
                      sx={{
                        bgcolor: 'grey.900',
                        height: 244,
                      }}
                      variant="rectangular"
                    />
                  </Box>
                </>
              ) : (
                <>
                  <Box className="col-span-6 md:col-span-3">
                    <Card>
                      <CardContent>
                        <Typography variant="h5">
                          Total CE Hours in {selectedCEBreakdownTimeframe}
                        </Typography>
                        <Typography variant="h2">
                          {(ceTotal?.[selectedCEBreakdownTimeframe] as any) ??
                            ''}
                        </Typography>
                        <Typography
                          color="text.secondary"
                          sx={{
                            mb: 1.5,
                          }}
                        >
                          across all users in your service
                        </Typography>
                      </CardContent>
                      <CardActions sx={{ p: 2 }}>
                        <FormControl variant="standard" fullWidth>
                          <InputLabel>Change timeframe</InputLabel>

                          <Select
                            value={selectedCEBreakdownTimeframe}
                            onChange={(e) =>
                              setSelectedCEBreakdownTimeframe(e.target.value)
                            }
                          >
                            <MenuItem value="">Change timeframe</MenuItem>
                            {timeframes.map((item) => (
                              <MenuItem value={item}>{item}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </CardActions>
                    </Card>
                  </Box>
                  <Box className="h-full col-span-6 md:col-span-3">
                    <Card className="h-full">
                      {user ? (
                        <>
                          <CardContent>
                            <Typography variant="h5">
                              Total Hours for {user.firstName} {user.lastName}{' '}
                              in {selectedCEBreakdownTimeframe}
                            </Typography>
                            <Typography variant="h2">
                              {(userCETotal?.[
                                selectedCEBreakdownTimeframe
                              ] as any) ?? 0}
                            </Typography>
                            <Typography
                              color="text.secondary"
                              sx={{
                                mb: 1.5,
                              }}
                            >
                              across all courses and categories
                            </Typography>
                          </CardContent>
                          <CardActions sx={{ p: 2 }}>
                            <Autocomplete
                              fullWidth
                              open={userTotalDropdownOpen}
                              onOpen={() => {
                                setUserTotalDropdownOpen(true)
                              }}
                              onClose={() => {
                                setUserTotalDropdownOpen(false)
                              }}
                              isOptionEqualToValue={(option, value) =>
                                option.primaryEmail === value.primaryEmail
                              }
                              getOptionLabel={(option) =>
                                `${option.name} | ${option.primaryEmail}`
                              }
                              // eslint-disable-next-line no-shadow
                              onInputChange={(_, value) => {
                                setSelectedUserEmail(value.split(' | ')[1])
                              }}
                              options={users}
                              loading={usersStatus === 'pending'}
                              renderInput={(params) => (
                                <TextField
                                  variant="standard"
                                  {...params}
                                  onChange={(e) =>
                                    setSearchValue(e.target.value)
                                  }
                                  label="Select a User"
                                  slotProps={{
                                    ...params.slotProps,

                                    input: {
                                      ...params.slotProps.input,
                                      endAdornment: (
                                        <>
                                          {
                                            <SearchIcon
                                              sx={{ cursor: 'pointer' }}
                                            />
                                          }
                                          {params.slotProps.input.endAdornment}
                                        </>
                                      ),
                                    },
                                  }}
                                />
                              )}
                            />
                            <FormControl variant="standard" fullWidth>
                              <InputLabel>Change timeframe</InputLabel>

                              <Select
                                value={selectedCEBreakdownTimeframe}
                                onChange={(e) =>
                                  setSelectedCEBreakdownTimeframe(
                                    e.target.value,
                                  )
                                }
                              >
                                <MenuItem value="">Change timeframe</MenuItem>
                                {timeframes.map((item) => (
                                  <MenuItem value={item}>{item}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </CardActions>
                        </>
                      ) : (
                        <>
                          <CardContent>
                            <Typography
                              color="text.secondary"
                              sx={{
                                mb: 1.5,
                              }}
                            >
                              Select a user to see thier total CE hours
                            </Typography>
                            <Typography variant="h5">&nbsp;</Typography>
                            <Typography variant="h2">&nbsp;</Typography>
                          </CardContent>
                          <CardActions sx={{ p: 2 }}>
                            <Autocomplete
                              fullWidth
                              open={userTotalDropdownOpen}
                              onOpen={() => {
                                setUserTotalDropdownOpen(true)
                              }}
                              onClose={() => {
                                setUserTotalDropdownOpen(false)
                              }}
                              isOptionEqualToValue={(option, value) =>
                                option.primaryEmail === value.primaryEmail
                              }
                              getOptionLabel={(option) =>
                                `${option.name} | ${option.primaryEmail}`
                              }
                              // eslint-disable-next-line no-shadow
                              onInputChange={(_, value) => {
                                setSelectedUserEmail(value.split(' | ')[1])
                              }}
                              options={users}
                              loading={usersStatus === 'pending'}
                              renderInput={(params) => (
                                <TextField
                                  variant="standard"
                                  {...params}
                                  onChange={(e) =>
                                    setSearchValue(e.target.value)
                                  }
                                  label="Select a User"
                                  slotProps={{
                                    ...params.slotProps,

                                    input: {
                                      ...params.slotProps.input,
                                      endAdornment: (
                                        <>
                                          {
                                            <SearchIcon
                                              sx={{ cursor: 'pointer' }}
                                            />
                                          }
                                          {params.slotProps.input.endAdornment}
                                        </>
                                      ),
                                    },
                                  }}
                                />
                              )}
                            />
                            <FormControl variant="standard" fullWidth>
                              <InputLabel>Change timeframe</InputLabel>

                              <Select
                                value={selectedCEBreakdownTimeframe}
                                onChange={(e) =>
                                  setSelectedCEBreakdownTimeframe(
                                    e.target.value,
                                  )
                                }
                              >
                                <MenuItem value="">Change timeframe</MenuItem>
                                {timeframes.map((item) => (
                                  <MenuItem value={item}>{item}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </CardActions>
                        </>
                      )}
                    </Card>
                  </Box>
                </>
              )}
            </Box>
            <Box className="grid grid-cols-6 gap-4">
              <Box className="col-span-6">
                <Paper>
                  <Typography
                    variant="h6"
                    sx={{
                      textAlign: 'center',
                      py: 2,
                      my: 2,
                    }}
                  >
                    Breakdown of CE Hours for {selectedLinkedService.name}
                  </Typography>
                </Paper>
              </Box>
              <Box className="relative col-span-6 min-h-96">
                {!ceBreakdown ? (
                  <Skeleton
                    sx={{
                      bgcolor: 'grey.900',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                    }}
                    variant="rectangular"
                  />
                ) : (
                  <Box sx={{ height: '100%' }}>
                    <BarChart
                      title="CE Hours per License Type"
                      dataset={
                        (ceBreakdown[selectedCEBreakdownTimeframe] as any) ??
                        ([] as any)
                      }
                      xAxis={[
                        {
                          scaleType: 'band',
                          dataKey: 'licenseType',
                          valueFormatter: (v) => v ?? '',
                        },
                      ]}
                      yAxis={[{ label: 'CE Hours' }]}
                      series={sectionNames.map((name) => ({
                        dataKey: name,
                        label: name,
                        stack: 'sections',
                        valueFormatter: (value) =>
                          value == null ? '0' : value.toLocaleString(),
                      }))}
                    />
                  </Box>
                )}
              </Box>
              <Box className="flex justify-center col-span-6" id="ce-timeframe">
                {!!ceBreakdown && (
                  <ToggleButtonGroup
                    value={selectedCEBreakdownTimeframe}
                    exclusive
                    onChange={handleSelectedCEBreakdownTimeframeChange}
                    aria-label="text alignment"
                  >
                    {timeframes.map((item) => (
                      <ToggleButton
                        key={item}
                        value={item}
                        aria-label="left aligned"
                      >
                        {item}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                )}
              </Box>
              <Box className="col-span-6">
                <Paper>
                  <Typography
                    variant="h6"
                    sx={{
                      textAlign: 'center',
                      py: 2,
                      my: 2,
                    }}
                  >
                    Breakdown of CE Hours for{' '}
                    {!user ? 'User' : `${user.firstName} ${user.lastName}`}
                  </Typography>
                </Paper>
              </Box>
              <Box className="col-span-6">
                <Autocomplete
                  open={userDropdownOpen}
                  onOpen={() => {
                    setUserDropdownOpen(true)
                  }}
                  onClose={() => {
                    setUserDropdownOpen(false)
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option.primaryEmail === value.primaryEmail
                  }
                  getOptionLabel={(option) =>
                    `${option.name} | ${option.primaryEmail}`
                  }
                  // eslint-disable-next-line no-shadow
                  onInputChange={(_, value) => {
                    setSelectedUserEmail(value.split(' | ')[1])
                  }}
                  options={users}
                  loading={usersStatus === 'pending'}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      onChange={(e) => setSearchValue(e.target.value)}
                      label="Select a User"
                      slotProps={{
                        ...params.slotProps,

                        input: {
                          ...params.slotProps.input,
                          endAdornment: (
                            <>
                              {<SearchIcon sx={{ cursor: 'pointer' }} />}
                              {params.slotProps.input.endAdornment}
                            </>
                          ),
                        },
                      }}
                    />
                  )}
                />
              </Box>
              <Box className="relative col-span-6 min-h-96">
                {!user ? (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="body1" color="text.secondary">
                      No user selected
                    </Typography>
                  </Box>
                ) : !userCEBreakdown ? (
                  <Skeleton
                    sx={{
                      bgcolor: 'grey.900',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                    }}
                    variant="rectangular"
                  />
                ) : (
                  <Box sx={{ height: '100%' }}>
                    <BarChart
                      title="User CE Hours by Month"
                      dataset={Object.values(userCEBreakdown).flat()}
                      xAxis={[
                        {
                          scaleType: 'band',
                          dataKey: 'timeFrame',
                          valueFormatter: (v) => v ?? '',
                        },
                      ]}
                      yAxis={[{ label: 'CE Hours' }]}
                      series={userSectionNames.map((name) => ({
                        dataKey: name,
                        label: name,
                        stack: 'sections',
                        valueFormatter: (value) =>
                          value == null ? '0' : value.toLocaleString(),
                      }))}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </>
        )}
      </Container>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  )
}
